import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const LoanTovalue = () => {
  const [loanAmount, setLoanAmount] = useState("");
  const [propertyValue, setPropertyValue] = useState("");
  const [ltvRatio, setLtvRatio] = useState("0.00%");

  // Function to calculate LTV Ratio
  const calculateLTV = (loan, value) => {
    if (value > 0) {
      return ((loan / value) * 100).toFixed(2) + "%";
    }
    return "0.00%";
  };

  // useEffect to recalculate LTV Ratio whenever inputs change
  useEffect(() => {
    const loan = parseFloat(loanAmount) || 0;
    const value = parseFloat(propertyValue) || 0;
    setLtvRatio(calculateLTV(loan, value));

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [loanAmount, propertyValue]);

  return (
    <Layout title="Loan-to-Value (LTV) Ratio Calculator" path="loan-to-value">
      <section
        className="ltv-calculator-section section-padding"
        id="ltv-calculator"
      >
        <div className="container">
          <div className="row">
            {/* LTV Ratio Calculator Section */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Loan-to-Value (LTV) Ratio Calculator
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Loan Amount */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="loanAmount" className="form-label">
                        Loan Amount (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Loan Amount:</strong> The total amount borrowed for the property."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="loanAmount"
                        step="0.01"
                        placeholder="Enter loan amount"
                        value={loanAmount}
                        onChange={(e) => setLoanAmount(e.target.value)}
                      />
                      {/* <small className="form-text text-muted">Enter the amount in Rupees (₹)</small> */}
                    </div>

                    {/* Appraised Property Value */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="propertyValue" className="form-label">
                        Appraised Property Value (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Property Value:</strong> The market value of the property being financed."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="propertyValue"
                        step="0.01"
                        placeholder="Enter appraised property value"
                        value={propertyValue}
                        onChange={(e) => setPropertyValue(e.target.value)}
                      />
                      {/* <small className="form-text text-muted">Enter the value in Rupees (₹)</small> */}
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        LTV Ratio:{" "}
                        <span id="ltvResult" className="fw-bold ms-3">
                          {ltvRatio}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Loan-to-Value (LTV) Ratio Calculator
                </h3>

                {/* What is a Loan-to-Value (LTV) Ratio Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is a Loan-to-Value (LTV) Ratio Calculator?
                </h4>
                <p>
                  A **Loan-to-Value (LTV) Ratio Calculator** is a tool used to
                  assess the risk associated with a loan by comparing the loan
                  amount to the appraised value of the property being used as
                  collateral. The result is expressed as a percentage.
                </p>

                {/* Why Use a Loan-to-Value (LTV) Ratio Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use a Loan-to-Value (LTV) Ratio Calculator?
                </h4>
                <p>
                  It offers benefits for both lenders and borrowers, such as:
                </p>
                <ul>
                  <li>
                    <strong>Risk Assessment:</strong> Helps lenders evaluate the
                    loan risk.
                  </li>
                  <li>
                    <strong>Loan Terms:</strong> Determines factors like
                    interest rates and down payment requirements.
                  </li>
                  <li>
                    <strong>Creditworthiness Evaluation:</strong> Assists in
                    determining the borrower’s repayment capability.
                  </li>
                  <li>
                    <strong>Responsible Borrowing:</strong> Aids borrowers in
                    understanding loan risks relative to property value.
                  </li>
                  <li>
                    <strong>Financial Insight:</strong> Helps users make
                    informed financial decisions.
                  </li>
                </ul>

                {/* How to Use a Loan-to-Value (LTV) Ratio Calculator? */}
                <h4 className="mt-4 mb-3">
                  How to Use a Loan-to-Value (LTV) Ratio Calculator?
                </h4>
                <p>To use the calculator, follow these simple steps:</p>
                <ul>
                  <li>Enter the **Loan Amount**.</li>
                  <li>Enter the **Appraised Property Value**.</li>
                  <li>View the calculated **LTV Ratio**.</li>
                </ul>

                {/* Additional Information */}
                <h4 className="mt-4 mb-3">
                  What Factors Affect the LTV Ratio?
                </h4>
                <p>The primary factors are:</p>
                <ul>
                  <li>
                    <strong>Loan Amount:</strong> The total borrowed amount.
                  </li>
                  <li>
                    <strong>Appraised Property Value:</strong> The property’s
                    market value.
                  </li>
                </ul>

                <h4 className="mt-4 mb-3">
                  Benefits of Understanding Your LTV Ratio
                </h4>
                <p>
                  Understanding your LTV ratio is crucial for both lenders and
                  borrowers to navigate loan conditions and assess risk.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default LoanTovalue;
